import z from "zod";

import { Gender, MaritalStatus, Title } from "../stores/yourDetails";
import { validateAnnuitiesPremium } from "./premium";
import {
    EscalationType,
    PaymentFrequency,
    PaySurvivingDependantType,
    TaxFreeType,
    ValueProtectOrGuarantee,
} from "../stores/pensionOptions";
import { MeasurementUnitLength, MeasurementUnitWeight, SmokerStatus } from "../stores/aboutYou";
import { PensionWiseBooking } from "../stores/mainView";
import { RelationshipToCustomer } from "../stores/dependantAboutYou";
import { MedicalCondition } from "./medicalValidator";

export const validateAddress = z.object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    city: z.string().nullable(),
    county: z.string().nullable(),
    postcode: z.string(),
});

const validateHeight = z.object({
    unit: z.nativeEnum(MeasurementUnitLength),
    value: z.number(),
}).optional();

const validateWeight = z.object({
    unit: z.nativeEnum(MeasurementUnitWeight),
    value: z.number(),
}).optional();

const validateWaist = z.object({
    unit: z.nativeEnum(MeasurementUnitLength),
    value: z.number(),
}).optional();

const validateSmoker = z.discriminatedUnion("smoker", [
    // never
    z.object({
        smoker: z.literal(SmokerStatus.Never),
    }),
    // previously
    z.object({
        smoker: z.literal(SmokerStatus.Previously),
        smoking: z.object({
            started: z.string(),
            ended: z.string(),
            cigarettesPerDay: z.number(),
            cigarsPerDay: z.number(),
            rollingTobaccoGramsPerWeek: z.number(),
            pipeTobaccoGramsPerWeek: z.number(),
        }),
    }),
    // currently
    z.object({
        smoker: z.literal(SmokerStatus.Currently),
        smoking: z.object({
            started: z.string(),
            cigarettesPerDay: z.number(),
            cigarsPerDay: z.number(),
            rollingTobaccoGramsPerWeek: z.number(),
            pipeTobaccoGramsPerWeek: z.number(),
        }),
    }),
]);

export const validateLifestyle =
    validateSmoker.and(
        z.object({
            alcoholUnitsPerWeek: z.number().optional(),
            height: validateHeight,
            weight: validateWeight,
            waist: validateWaist,
        }),
    ).nullish();

const optionalValidateLifestyle = z.union([
    z.object({
        alcoholUnitsPerWeek: z.number().optional(),
        height: validateHeight.optional(),
        weight: validateWeight,
        waist: validateWaist,
    }).partial(),
    validateSmoker.optional(),
]);

export type Lifestyle = z.infer<typeof validateLifestyle>;
export type OptionalLifestyle = z.infer<typeof optionalValidateLifestyle>;
export type Height = z.infer<typeof validateHeight>;
export type Weight = z.infer<typeof validateWeight>;
export type Waist = z.infer<typeof validateWaist>;
export type Smoking = z.infer<typeof validateSmoker>;

const validateDailyActivities = z.object({
    dressYourself: z.string().nullable(),
    applicantMobility: z.string().nullable(),
    applicantTransferring: z.string().nullable(),
    applicantBladder: z.string().nullable(),
    applicantBowels: z.string().nullable(),
    applicantBathing: z.string().nullable(),
    applicantFeeding: z.string().nullable(),
    applicantProgression: z.string().nullable(),
});

export const annuitiesCustomer = z.object({
    title: z.nativeEnum(Title).nullable(),
    forename: z.string(),
    surname: z.string(),
    phone: z.string().nullable(),
    dob: z.string(),
    email: z.string().email(),
    sex: z.nativeEnum(Gender).nullish(),
    maritalStatus: z.nativeEnum(MaritalStatus).nullable(),
    address: validateAddress,
    lifestyle: validateLifestyle.nullish(),
    medical: z.optional(z.array(MedicalCondition)),
    dailyActivities: z.optional(validateDailyActivities),
});

const validateTaxFree = z.discriminatedUnion("type", [
    z.object({
        type: z.literal(TaxFreeType.Full),
        fraction: z.number(),
        value: z.number(),
    }), z.object({
        type: z.literal(TaxFreeType.Absolute),
        value: z.number(),
    }),
]);

const validateEscalation = z.discriminatedUnion("type", [
    z.object({
        type: z.enum([EscalationType.LimitedPriceIndexation, EscalationType.RetailPriceIndex, EscalationType.None]),
    }),
    z.object({
        type: z.literal(EscalationType.Fixed),
        value: z.number(),
    }),
]);

const validateGuarantee = z.discriminatedUnion("type", [
    z.object({
        type: z.literal(ValueProtectOrGuarantee.None),
    }), z.object({
        type: z.literal(ValueProtectOrGuarantee.ValueProtection),
        fraction: z.number(),
        endingAt: z.string(),
    }), z.object({
        type: z.literal(ValueProtectOrGuarantee.GuaranteePeriod),
        years: z.number(),
    }),
]);

const validatePaySurvivingDependant = z.discriminatedUnion("type", [
    z.object({
        type: z.literal(PaySurvivingDependantType.None),
    }),
    z.object({
        type: z.literal(PaySurvivingDependantType.Fraction),
        value: z.number(),
    }),
]);

const annuitiesProduct = z.object({
    totalFund: z.number(),
    annuityExceedMaximum: z.boolean().nullable().optional(),
    acknowledgementILSA: z.boolean().nullable().optional(),
    paymentFrequency: z.nativeEnum(PaymentFrequency),
    paymentInArrears: z.boolean(),
    proportional: z.boolean().optional(),
    paySurvivingDependant: validatePaySurvivingDependant,
    guarantee: validateGuarantee,
    taxFree: validateTaxFree,
    escalation: validateEscalation,
    overlap: z.boolean().optional(),
    consent: z.boolean(),
    pensionEligibility: z.object({
        pensionType: z.boolean().optional(),
        fundTransferred: z.boolean().optional(),
        guaranteeAnnuityRate: z.boolean().optional(),
        moreThanTwentyFive: z.boolean().optional(),
    }).optional(),
});

export type TaxFree = z.infer<typeof validateTaxFree>;
export type Escalation = z.infer<typeof validateEscalation>;
export type Guarantee = z.infer<typeof validateGuarantee>;
export type PaySurvivingDependant = z.infer<typeof validatePaySurvivingDependant>;

export const annuitiesDependent = annuitiesCustomer.omit((
    {
        email: true,
        phone: true,
        maritalStatus: true,
        title: true,
        forename: true,
        surname: true,
        sex: true,
    })).merge(z.object({
    title: z.nativeEnum(Title).optional(),
    forename: z.string().optional(),
    surname: z.string().optional(),
    sex: z.nativeEnum(Gender).nullish(),
    relationshipToCustomer: z.nativeEnum(RelationshipToCustomer).optional(),
    maritalStatus: z.nativeEnum(MaritalStatus).nullable(),
}));

export const annuitiesDependantPartial = z.union([
    annuitiesCustomer.extend({
        relationshipToCustomer: z.nativeEnum(RelationshipToCustomer).optional(),
    }).omit({
        email: true,
        phone: true,
    }).partial(),
    z.object({
        lifestyle: optionalValidateLifestyle,
    }),
]);

export const affiliateFormat = z.object({
    name: z.string(),
    number: z.number(),
    source: z.string().optional(),
    sourceId: z.number().nullable().default(null),
});

export const validateAnnuityQuoteRequestFormat = z.object({
    quoteGeneratedBy: z.enum(["Extranet", "AMS", "Annuity Ready"]).nullable().optional(),
    quoteTime: z.string().optional(),
    appointmentChoice: z.nativeEnum(PensionWiseBooking).nullable(),
    device: z.enum(["mobile", "tablet", "desktop"]).optional(),
    affiliate: affiliateFormat,
    dependant: annuitiesDependent.optional(),
    customer: annuitiesCustomer,
    product: annuitiesProduct,
    async: z.boolean().optional(),
    initiatorId: z.string().nullish(),
    agencyNumber: z.coerce.string().max(7).nullish(),
    tags: z.record(z.string())
        .optional()
        .default({}),
}).passthrough().superRefine((val, ctx) => {
    // only validate dependant if it's needed
    if (val.product.paySurvivingDependant.type !== PaySurvivingDependantType.None) {
        const dependantResult = annuitiesDependent.safeParse(val.dependant);
        if (dependantResult.success && val.product.guarantee.type === ValueProtectOrGuarantee.GuaranteePeriod) {
            if (typeof val.product.overlap === "undefined") {
                ctx.addIssue({
                    code: z.ZodIssueCode.invalid_type,
                    expected: z.ZodParsedType.boolean,
                    received: z.ZodParsedType.undefined,
                    path: ["overlap"],
                });
            }
        }
        if (!dependantResult.success) {
            dependantResult.error.issues.map((e) => ctx.addIssue({
                ...e,
                path: [
                    "dependant",
                    ...e.path],
            }));
        }
    }

    if (val.product.paymentFrequency !== PaymentFrequency.Monthly && val.product.paymentInArrears === true) {
        if (typeof val.product.proportional === "undefined") {
            ctx.addIssue({
                code: z.ZodIssueCode.invalid_type,
                expected: z.ZodParsedType.boolean,
                received: z.ZodParsedType.undefined,
                path: ["proportional"],
            });
        }
    }
});
/**
 * Validate a partial post from annuities apply journey accordion
 */
export const validatePartialQuoteRequestFormat = z.object({
    device: z.enum(["mobile", "desktop", "tablet"] as const),
    affiliate: affiliateFormat,
    customer: annuitiesCustomer.or(annuitiesCustomer.extend({ lifestyle: z.object({}) })),
    product: annuitiesProduct.partial(),
    dependant: annuitiesDependantPartial,
    affiliateData: z.any().optional(),
    tags: z.any().optional(),
});

/**
 * Validate pension wise modal from annuities apply journey post quote
 */
export const validateNudgeRequestFormat = z.object({
    appointmentChoice: z.nativeEnum(PensionWiseBooking).nullable(),
});

export type Affiliate = z.infer<typeof affiliateFormat>;
export type Quote = z.infer<typeof validateAnnuityQuoteRequestFormat>;
export type QuoteRequest = z.infer<typeof validateAnnuityQuoteRequestFormat>;
export const partialQuote = validatePartialQuoteRequestFormat;
export type PartialForm = z.infer<typeof validatePartialQuoteRequestFormat>;
export const nudgeRequest = validateNudgeRequestFormat;

export const validateQuoteResponse = z.object({
    response: z.object({
        finished: z.boolean(),
        quotations: z.array(validateAnnuitiesPremium),
        token: z.string(),
        quoteNumber: z.string(),
        quote: validateAnnuityQuoteRequestFormat.optional(),
        // NOTE panel only comes back on the initial quote requests
        panel: z.object({
            size: z.number(),
        }).optional(),
    }),
});

export type QuoteResponse = z.infer<typeof validateQuoteResponse>;

export const validateMyQuoteResponse = z.object({
    pagination: z.object({
        current: z.string(),
    }),
    quotes: z.array(
        z.object({
            quoteNumber: z.string(),
            quote: validateAnnuityQuoteRequestFormat,
            product: z.literal("annuities"),
            quotationsCount: z.number(),
            quotations: z.array(validateAnnuitiesPremium),
        }),
    ),
});
